import { VacancySummary } from './vacancy-document';
import { displayCity, displaySector, formatEuro, postedDate, salaryRange } from './vacancy-format';

/**
 * The /vacatures filter model. Options come from the jobs themselves (so a filter can never offer a
 * value no job has), values within one filter combine with OR, and filters combine with AND. Every
 * option carries the number of jobs it would show given all *other* active filters.
 */
export type FacetKey = 'industries' | 'sector' | 'education' | 'hours' | 'employment' | 'salary' | 'datePosted';

export interface VacancyFilterState {
  q: string;
  location: string;
  industries: string[];
  sector: string[];
  education: string[];
  hours: string[];
  employment: string[];
  /** Minimum monthly salary, e.g. "4000". */
  salary: string;
  /** Posted within N days ("3", "7", "14") or "ouder" (more than 14 days ago). */
  datePosted: string;
  /** 1-based. */
  page: number;
}

export const EMPTY_FILTERS: VacancyFilterState = {
  q: '',
  location: '',
  industries: [],
  sector: [],
  education: [],
  hours: [],
  employment: [],
  salary: '',
  datePosted: '',
  page: 1,
};

export interface FacetOption {
  value: string;
  label: string;
  count: number;
}

export interface Facet {
  key: FacetKey;
  label: string;
  /** Label of the "no filter" choice for single-choice facets. */
  anyLabel?: string;
  multiple: boolean;
  options: FacetOption[];
}

export interface ActiveFilter {
  key: FacetKey | 'q' | 'location';
  value: string;
  label: string;
}

export interface Relaxation {
  filter: ActiveFilter;
  count: number;
}

export type TitleMatcher = (title: string, query: string) => boolean;

const MULTI_KEYS = ['industries', 'sector', 'education', 'hours', 'employment'] as const;
type MultiKey = (typeof MULTI_KEYS)[number];

const SALARY_STEPS = [2000, 3000, 4000, 5000, 6000, 7000];
const DATE_BUCKETS: { value: string; label: string }[] = [
  { value: '3', label: 'Afgelopen 3 dagen' },
  { value: '7', label: 'Afgelopen 7 dagen' },
  { value: '14', label: 'Afgelopen 14 dagen' },
  { value: 'ouder', label: 'Meer dan 14 dagen geleden' },
];
/** Earlier URLs carried the Dutch labels themselves. */
const LEGACY_DATE_VALUES: Record<string, string> = Object.fromEntries(DATE_BUCKETS.map((bucket) => [bucket.label, bucket.value]));

const FACET_LABELS: Record<FacetKey, string> = {
  industries: 'Functiegebied',
  sector: 'Sector',
  education: 'Opleidingsniveau',
  hours: 'Uren per week',
  employment: 'Dienstverband',
  salary: 'Salaris',
  datePosted: 'Geplaatst',
};

const DAY_MS = 24 * 60 * 60 * 1000;

function multiValues(job: VacancySummary, key: MultiKey): string[] {
  switch (key) {
    case 'industries':
      return (job.industries ?? []).filter(Boolean);
    case 'sector': {
      const sector = displaySector(job);
      return sector ? [sector] : [];
    }
    case 'education':
      return job.education ? [job.education] : [];
    case 'hours':
      return (job.hours ?? []).map((hour) => String(hour).trim()).filter(Boolean);
    case 'employment':
      return job.employment ? [job.employment] : [];
  }
}

/** Lowercase, no diacritics or separators, and common names mapped onto the official ones. */
const PLACE_ALIASES: [RegExp, string][] = [
  [/denhaag/g, 'sgravenhage'],
  [/thehague/g, 'sgravenhage'],
  [/denbosch/g, 'shertogenbosch'],
];

export function normalizePlace(value: string): string {
  let normalized = (value ?? '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
  for (const [alias, official] of PLACE_ALIASES) normalized = normalized.replace(alias, official);
  return normalized;
}

export function matchesLocation(job: VacancySummary, query: string): boolean {
  const wanted = normalizePlace(query);
  if (!wanted) return true;
  const postal = (job.location?.postalCode ?? '').replace(/\s+/g, '').toLowerCase();
  return (
    normalizePlace(job.location?.city ?? '').includes(wanted) ||
    normalizePlace(job.location?.state ?? '').includes(wanted) ||
    (postal !== '' && postal.startsWith(query.replace(/\s+/g, '').toLowerCase()))
  );
}

function daysSincePosted(job: VacancySummary, now: Date): number | null {
  const date = postedDate(job);
  return date ? (now.getTime() - date.getTime()) / DAY_MS : null;
}

function matchesDate(job: VacancySummary, bucket: string, now: Date): boolean {
  if (!bucket) return true;
  const days = daysSincePosted(job, now);
  if (days === null) return false;
  return bucket === 'ouder' ? days > 14 : days <= Number(bucket);
}

function matchesSalary(job: VacancySummary, minimum: string): boolean {
  if (!minimum) return true;
  const range = salaryRange(job.salary);
  return !!range && range.max >= Number(minimum);
}

/** Every filter except `skip`; `skip` lets a facet count its own options. */
function matches(job: VacancySummary, state: VacancyFilterState, matchesTitle: TitleMatcher, now: Date, skip?: FacetKey | 'q' | 'location'): boolean {
  if (skip !== 'q' && state.q && !matchesTitle(job.title ?? '', state.q)) return false;
  if (skip !== 'location' && state.location && !matchesLocation(job, state.location)) return false;
  for (const key of MULTI_KEYS) {
    if (key === skip || state[key].length === 0) continue;
    const values = multiValues(job, key);
    if (!state[key].some((selected) => values.includes(selected))) return false;
  }
  if (skip !== 'salary' && !matchesSalary(job, state.salary)) return false;
  if (skip !== 'datePosted' && !matchesDate(job, state.datePosted, now)) return false;
  return true;
}

function newestFirst(a: VacancySummary, b: VacancySummary): number {
  return (postedDate(b)?.getTime() ?? 0) - (postedDate(a)?.getTime() ?? 0);
}

/** Filtered jobs: title matches of the search term first, newest first within each group. */
export function filterJobs(jobs: readonly VacancySummary[], state: VacancyFilterState, matchesTitle: TitleMatcher, now: Date = new Date()): VacancySummary[] {
  const result = jobs.filter((job) => matches(job, state, matchesTitle, now));
  const query = state.q.trim().toLowerCase();
  const direct = (job: VacancySummary) => (query && (job.title ?? '').toLowerCase().includes(query) ? 1 : 0);
  return result.sort((a, b) => direct(b) - direct(a) || newestFirst(a, b));
}

function countBy(jobs: VacancySummary[], valuesOf: (job: VacancySummary) => string[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const job of jobs) for (const value of new Set(valuesOf(job))) counts.set(value, (counts.get(value) ?? 0) + 1);
  return counts;
}

function sortOptions(key: MultiKey, options: FacetOption[]): FacetOption[] {
  if (key === 'hours') return options.sort((a, b) => Number(b.value) - Number(a.value));
  if (key === 'education') {
    const order = ['MBO', 'HBO', 'WO'];
    const rank = (value: string) => (order.indexOf(value) === -1 ? order.length : order.indexOf(value));
    return options.sort((a, b) => rank(a.value) - rank(b.value) || a.label.localeCompare(b.label, 'nl'));
  }
  return options.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'nl'));
}

/**
 * Facets worth showing. Options are the values the jobs actually have (so a value no job has is never
 * offered) in a stable order; counts follow the other active filters and may drop to 0, which keeps
 * the controls in place while narrowing. A facet where every job has the same value is hidden.
 */
export function buildFacets(jobs: readonly VacancySummary[], state: VacancyFilterState, matchesTitle: TitleMatcher, now: Date = new Date()): Facet[] {
  const facets: Facet[] = [];
  const all = [...jobs];

  const multiFacet = (key: MultiKey) => {
    const everywhere = countBy(all, (job) => multiValues(job, key));
    const selected = state[key];
    const canNarrow = [...everywhere.values()].some((count) => count < all.length) || everywhere.size > 1;
    if (!canNarrow && selected.length === 0) return;
    const scoped = countBy(all.filter((job) => matches(job, state, matchesTitle, now, key)), (job) => multiValues(job, key));
    const values = new Set([...everywhere.keys(), ...selected]);
    const ordered = sortOptions(key, [...values].map((value) => ({ value, label: value, count: everywhere.get(value) ?? 0 })));
    const options = ordered.map(({ value }) => ({ value, label: key === 'hours' ? `${value} uur` : value, count: scoped.get(value) ?? 0 }));
    if (options.length === 0) return;
    facets.push({ key, label: FACET_LABELS[key], multiple: true, options });
  };

  multiFacet('industries');
  multiFacet('sector');

  const salaryScope = all.filter((job) => matches(job, state, matchesTitle, now, 'salary'));
  const salaryOptions = SALARY_STEPS.filter((step) => all.some((job) => matchesSalary(job, String(step))) || String(step) === state.salary).map((step) => ({
    value: String(step),
    label: `Vanaf ${formatEuro(step)}`,
    count: salaryScope.filter((job) => matchesSalary(job, String(step))).length,
  }));
  if (salaryOptions.length > 0) {
    facets.push({ key: 'salary', label: FACET_LABELS.salary, anyLabel: 'Elk salaris', multiple: false, options: salaryOptions });
  }

  multiFacet('education');
  multiFacet('hours');
  multiFacet('employment');

  const dateScope = all.filter((job) => matches(job, state, matchesTitle, now, 'datePosted'));
  const dateOptions = DATE_BUCKETS.filter((bucket) => all.some((job) => matchesDate(job, bucket.value, now)) || bucket.value === state.datePosted).map((bucket) => ({
    ...bucket,
    count: dateScope.filter((job) => matchesDate(job, bucket.value, now)).length,
  }));
  if (dateOptions.length > 0) {
    facets.push({ key: 'datePosted', label: FACET_LABELS.datePosted, anyLabel: 'Altijd', multiple: false, options: dateOptions });
  }
  return facets;
}

export function activeFilters(state: VacancyFilterState): ActiveFilter[] {
  const active: ActiveFilter[] = [];
  if (state.q) active.push({ key: 'q', value: state.q, label: `“${state.q}”` });
  if (state.location) active.push({ key: 'location', value: state.location, label: displayCity(state.location) });
  for (const key of MULTI_KEYS) {
    for (const value of state[key]) active.push({ key, value, label: key === 'hours' ? `${value} uur` : value });
  }
  if (state.salary) active.push({ key: 'salary', value: state.salary, label: `Vanaf ${formatEuro(Number(state.salary))}` });
  if (state.datePosted) {
    const bucket = DATE_BUCKETS.find((option) => option.value === state.datePosted);
    if (bucket) active.push({ key: 'datePosted', value: state.datePosted, label: bucket.label });
  }
  return active;
}

export function withoutFilter(state: VacancyFilterState, filter: ActiveFilter): VacancyFilterState {
  const next: VacancyFilterState = { ...state, page: 1 };
  switch (filter.key) {
    case 'q':
    case 'location':
    case 'salary':
    case 'datePosted':
      next[filter.key] = '';
      return next;
    default:
      next[filter.key] = state[filter.key].filter((value) => value !== filter.value);
      return next;
  }
}

/** For an empty result: which single filter to drop, and how many jobs that would show. */
export function relaxations(jobs: readonly VacancySummary[], state: VacancyFilterState, matchesTitle: TitleMatcher, now: Date = new Date()): Relaxation[] {
  return activeFilters(state)
    .map((filter) => ({ filter, count: filterJobs(jobs, withoutFilter(state, filter), matchesTitle, now).length }))
    .filter((relaxation) => relaxation.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);
}

// --- URL (de)serialisation -------------------------------------------------------------------

function list(value: string | null): string[] {
  return (value ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

export interface QueryParamReader {
  get(name: string): string | null;
}

export function filtersFromParams(params: QueryParamReader): VacancyFilterState {
  const salary = (params.get('salary') ?? '').replace(/[^0-9]/g, '');
  const rawDate = params.get('datePosted') ?? '';
  const datePosted = LEGACY_DATE_VALUES[rawDate] ?? rawDate;
  const page = Number.parseInt(params.get('page') ?? '', 10);
  return {
    q: (params.get('q') ?? '').trim(),
    location: (params.get('location') ?? '').trim(),
    industries: list(params.get('industries')),
    sector: list(params.get('sector')),
    education: list(params.get('education')),
    hours: list(params.get('hours')).map((hour) => hour.replace(/[^0-9]/g, '')).filter(Boolean),
    employment: list(params.get('employment')),
    salary: SALARY_STEPS.includes(Number(salary)) ? salary : '',
    datePosted: DATE_BUCKETS.some((bucket) => bucket.value === datePosted) ? datePosted : '',
    page: Number.isFinite(page) && page > 1 ? page : 1,
  };
}

/** Query params for a state; empty values are null so the router drops them. */
export function filtersToParams(state: VacancyFilterState): Record<string, string | null> {
  const joined = (values: string[]) => (values.length ? values.join(',') : null);
  return {
    q: state.q || null,
    location: state.location || null,
    industries: joined(state.industries),
    sector: joined(state.sector),
    education: joined(state.education),
    hours: joined(state.hours),
    employment: joined(state.employment),
    salary: state.salary || null,
    datePosted: state.datePosted || null,
    page: state.page > 1 ? String(state.page) : null,
  };
}

export function hasActiveFilters(state: VacancyFilterState): boolean {
  return activeFilters(state).length > 0;
}
